<template>
  <div class="geo-map-panel">
    <div class="map-panel-head">
      <div class="map-head-left">
        <v-icon size="16" color="#1b5e20" class="mr-2">mdi-map-outline</v-icon>
        <span class="map-panel-title">แผนที่สถานศึกษาทั่วประเทศ</span>
      </div>
      <div class="map-head-right">
        <div class="zoom-btns">
          <button class="zoom-btn" @click="mapZoomIn" title="ขยาย"><v-icon size="14">mdi-plus</v-icon></button>
          <button class="zoom-btn" @click="mapZoomOut" title="ย่อ"><v-icon size="14">mdi-minus</v-icon></button>
          <button class="zoom-btn" @click="mapFit" title="รีเซ็ต"><v-icon size="14">mdi-home-outline</v-icon></button>
        </div>
      </div>
    </div>

    <div class="map-body">
      <div class="map-wrap" ref="mapWrap">
        <div id="th-map-colleges" ref="thMap" class="th-map"></div>

        <svg class="bubble-overlay" ref="bubbleSvg">
          <g v-for="b in bubbles" :key="b.pid">
            <circle
              :cx="b.x" :cy="b.y" :r="b.r"
              fill="#16a34a55"
              stroke="white"
              :stroke-width="b.r > 10 ? 1.5 : 1"
              opacity="0.85"
              class="bubble-circle"
              @mouseenter="showBubbleTip($event, b)"
              @mouseleave="hideTip"
              @click="onProvinceClick(b)"
              style="cursor:pointer"
            />
            <text v-if="b.r > 14" :x="b.x" :y="b.y + 4" text-anchor="middle" class="bubble-label">{{ b.shortLabel }}</text>
          </g>
        </svg>

        <transition name="tip-fade">
          <div v-if="tooltip.show" class="map-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
            <div class="tip-header">
              <span class="tip-province">{{ tooltip.province }}</span>
              <span class="tip-region">{{ tooltip.region }}</span>
            </div>
            <div class="tip-body">
              <div class="tip-row"><span>สถานศึกษา</span><strong>{{ tooltip.total }} แห่ง</strong></div>
            </div>
          </div>
        </transition>

        <transition name="tip-fade">
          <div v-if="selectedProv" class="selected-info">
            <div class="sel-title">
              <v-icon size="14" color="#1b5e20" class="mr-1">mdi-map-marker</v-icon>
              <strong>{{ selectedProv.province }}</strong>
            </div>
            <div class="sel-stats">
              <div class="sel-stat"><span>สถานศึกษา</span><strong>{{ selectedProv.total }} แห่ง</strong></div>
            </div>
            <div class="sel-actions">
              <v-btn x-small text color="success" @click="$emit('filter-province', selectedProv)">กรองข้อมูล</v-btn>
              <v-btn icon x-small @click="selectedProv = null"><v-icon size="12">mdi-close</v-icon></v-btn>
            </div>
          </div>
        </transition>
      </div>

      <div class="map-side">
        <div class="ranking-box">
          <div class="ranking-title">
            <v-icon size="13" color="#374151" class="mr-1">mdi-format-list-numbered</v-icon>
            Top 10 จังหวัดที่มีสถานศึกษามากที่สุด
          </div>
          <div class="ranking-list">
            <div
              v-for="(p, i) in topProvinces"
              :key="p.province"
              class="rank-row"
              :class="{ 'rank-row--active': selectedProv && selectedProv.province === p.province }"
              @click="onRankClick(p)"
            >
              <span class="rank-num" :class="i < 3 ? 'rank-num--top' : ''">{{ i + 1 }}</span>
              <span class="rank-name">{{ p.province }}</span>
              <div class="rank-bar-wrap"><div class="rank-bar-fill" :style="`width:${p.pct}%;background:#2e7d32`"></div></div>
              <span class="rank-val">{{ p.total }}</span>
            </div>
          </div>
        </div>

        <div class="map-stats-grid">
          <div class="map-stat-card">
            <div class="map-stat-val" style="color:#1b5e20">{{ provinceCount }}</div>
            <div class="map-stat-label">จังหวัดที่มีสถานศึกษา</div>
          </div>
          <div class="map-stat-card">
            <div class="map-stat-val" style="color:#1b5e20">{{ totalColleges }}</div>
            <div class="map-stat-label">สถานศึกษาทั้งหมด</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ── Province metadata: province_name → centroid [lat, lng] ──
   คัดลอกจาก GeoMapDashboard.vue เพื่อความสอดคล้องกัน (ข้อมูลเดิมใช้ได้) */
const PROVINCE_CENTROIDS = {
  'กรุงเทพมหานคร':[13.753,100.504],'สมุทรปราการ':[13.599,100.597],'นนทบุรี':[13.86,100.512],
  'ปทุมธานี':[14.018,100.52],'พระนครศรีอยุธยา':[14.35,100.585],'อ่างทอง':[14.59,100.455],
  'ลพบุรี':[14.799,100.653],'สิงห์บุรี':[14.89,100.397],'ชัยนาท':[15.186,100.129],
  'สระบุรี':[14.529,100.907],'ชลบุรี':[13.361,100.985],'ระยอง':[12.681,101.281],
  'จันทบุรี':[12.611,102.104],'ตราด':[12.243,102.518],'ฉะเชิงเทรา':[13.689,101.077],
  'ปราจีนบุรี':[14.051,101.37],'นครนายก':[14.204,101.213],'สระแก้ว':[13.824,102.065],
  'นครราชสีมา':[14.973,102.098],'บุรีรัมย์':[14.994,103.102],'สุรินทร์':[14.882,103.493],
  'ศรีสะเกษ':[15.117,104.322],'อุบลราชธานี':[15.245,104.847],'ยโสธร':[15.793,104.145],
  'ชัยภูมิ':[15.807,102.032],'อำนาจเจริญ':[15.865,104.627],'บึงกาฬ':[18.36,103.648],
  'หนองบัวลำภู':[17.204,102.441],'ขอนแก่น':[16.432,102.836],'อุดรธานี':[17.41,102.787],
  'เลย':[17.486,101.722],'หนองคาย':[17.877,102.742],'มหาสารคาม':[16.185,103.3],
  'ร้อยเอ็ด':[16.054,103.652],'กาฬสินธุ์':[16.431,103.506],'สกลนคร':[17.155,104.136],
  'นครพนม':[17.392,104.773],'มุกดาหาร':[16.543,104.723],'เชียงใหม่':[18.788,98.984],
  'ลำพูน':[18.575,99.009],'ลำปาง':[18.289,99.491],'อุตรดิตถ์':[17.627,100.099],
  'แพร่':[18.145,100.141],'น่าน':[18.784,100.779],'พะเยา':[19.156,99.893],
  'เชียงราย':[19.908,99.831],'แม่ฮ่องสอน':[19.302,97.964],'นครสวรรค์':[15.703,100.137],
  'อุทัยธานี':[15.38,100.024],'กำแพงเพชร':[16.482,99.524],'ตาก':[16.879,99.126],
  'สุโขทัย':[17.007,99.825],'พิษณุโลก':[16.822,100.265],'พิจิตร':[16.441,100.348],
  'เพชรบูรณ์':[16.42,101.159],'ราชบุรี':[13.537,99.814],'กาญจนบุรี':[14.005,99.548],
  'สุพรรณบุรี':[14.472,100.117],'นครปฐม':[13.82,100.044],'สมุทรสาคร':[13.548,100.274],
  'สมุทรสงคราม':[13.409,100.002],'เพชรบุรี':[12.965,99.647],'ประจวบคีรีขันธ์':[11.806,99.798],
  'นครศรีธรรมราช':[8.432,99.963],'กระบี่':[8.093,98.906],'พังงา':[8.451,98.526],
  'ภูเก็ต':[7.89,98.398],'สุราษฎร์ธานี':[9.14,99.331],'ระนอง':[9.965,98.638],
  'ชุมพร':[10.493,99.18],'สงขลา':[7.189,100.595],'สตูล':[6.614,100.067],
  'ตรัง':[7.557,99.611],'พัทลุง':[7.616,100.082],'ปัตตานี':[6.870,101.246],
  'ยะลา':[6.535,101.279],'นราธิวาส':[6.425,101.824],
};

const THAILAND_GEOJSON_URL = 'https://raw.githubusercontent.com/apisit/thailand.json/master/thailand.json';

export default {
  name: "CollegeMapDashboard",
  props: {
    byProvince: { type: Array, default: () => [] } // [{ province_name, region_name, total }]
  },
  data() {
    return {
      leafletMap: null,
      leafletGeoLayer: null,
      geoData: null,
      tooltip: { show: false, x: 0, y: 0, province: "", region: "", total: 0 },
      selectedProv: null,
      bubbles: []
    };
  },
  computed: {
    provinceMap() {
      const map = {};
      this.byProvince.forEach(r => {
        if (!r.province_name) return;
        map[r.province_name] = { province: r.province_name, region: r.region_name || "", total: +r.total || 0 };
      });
      return map;
    },
    maxTotal() {
      return Math.max(...Object.values(this.provinceMap).map(p => p.total), 1);
    },
    provinceCount() {
      return Object.keys(this.provinceMap).length;
    },
    totalColleges() {
      return Object.values(this.provinceMap).reduce((s, p) => s + p.total, 0);
    },
    topProvinces() {
      const entries = Object.values(this.provinceMap);
      const sorted = [...entries].sort((a, b) => b.total - a.total).slice(0, 10);
      const max = this.maxTotal;
      return sorted.map(p => ({ ...p, pct: Math.round((p.total / max) * 100) }));
    }
  },
  watch: {
    byProvince() {
      this.refreshMap();
      this.buildBubbles();
    }
  },
  mounted() {
    this.$nextTick(async () => {
      await this.initMap();
      this.buildBubbles();
    });
  },
  beforeDestroy() {
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
  },
  methods: {
    async initMap() {
      const el = this.$refs.thMap;
      if (!el || this.leafletMap) return;
      this.leafletMap = L.map(el, {
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: true,
        minZoom: 5,
        maxZoom: 11
      });
      this.leafletMap.fitBounds([[5.5, 97.3], [20.5, 105.7]], { padding: [8, 8] });
      this.leafletMap.on("moveend zoomend", () => this.buildBubbles());
      try {
        const res = await fetch(THAILAND_GEOJSON_URL);
        this.geoData = await res.json();
        this.renderGeoLayer();
      } catch (e) {
        console.error("GeoJSON load error:", e);
      }
    },

    getProvinceColor(provinceName) {
      const p = this.provinceMap[provinceName];
      if (!p) return "#e4e4e7";
      const t = Math.min(p.total / this.maxTotal, 1);
      const ah = 0xdcfce7, bh = 0x166534;
      const ar = (ah >> 16) & 255, ag = (ah >> 8) & 255, ab = ah & 255;
      const br = (bh >> 16) & 255, bg = (bh >> 8) & 255, bb = bh & 255;
      const r = Math.round(ar + (br - ar) * t);
      const g = Math.round(ag + (bg - ag) * t);
      const b = Math.round(ab + (bb - ab) * t);
      return "#" + [r, g, b].map(v => v.toString(16).padStart(2, "0")).join("");
    },

    renderGeoLayer() {
      if (!this.leafletMap || !this.geoData) return;
      if (this.leafletGeoLayer) {
        this.leafletGeoLayer.remove();
        this.leafletGeoLayer = null;
      }
      this.leafletGeoLayer = L.geoJSON(this.geoData, {
        style: f => ({
          fillColor: this.getProvinceColor(this.getProvName(f)),
          fillOpacity: 0.75,
          color: "#ffffff",
          weight: 0.8,
          opacity: 1
        }),
        onEachFeature: (f, layer) => {
          const name = this.getProvName(f);
          layer.on({
            mousemove: e => this.onLayerHover(e, name),
            mouseout: () => {
              this.tooltip.show = false;
            },
            click: () => {
              const p = this.provinceMap[name];
              if (p) this.selectedProv = { ...p };
            }
          });
        }
      }).addTo(this.leafletMap);
    },

    refreshMap() {
      if (!this.leafletGeoLayer) return;
      this.leafletGeoLayer.eachLayer(layer => {
        if (layer.feature) {
          layer.setStyle({
            fillColor: this.getProvinceColor(this.getProvName(layer.feature)),
            fillOpacity: 0.75,
            color: "#ffffff",
            weight: 0.8
          });
        }
      });
    },

    getProvName(feature) {
      const p = feature.properties;
      return p.prov_nameTH || p.name_th || p.name || p.NAME_1 || "";
    },

    onLayerHover(e, name) {
      const p = this.provinceMap[name];
      if (!p) return;
      const wrap = this.$refs.mapWrap;
      const rect = wrap ? wrap.getBoundingClientRect() : { left: 0, top: 0 };
      this.tooltip = {
        show: true,
        x: e.originalEvent.clientX - rect.left + 16,
        y: e.originalEvent.clientY - rect.top - 60,
        province: p.province,
        region: p.region,
        total: p.total
      };
    },

    buildBubbles() {
      if (!this.leafletMap) return;
      const svgEl = this.$refs.bubbleSvg;
      if (!svgEl) return;
      const rect = svgEl.getBoundingClientRect();
      const W = rect.width || 400, H = rect.height || 400;
      const maxTotal = this.maxTotal || 1;
      const bubbles = [];
      Object.entries(this.provinceMap).forEach(([name, p]) => {
        const centroid = PROVINCE_CENTROIDS[name];
        if (!centroid) return;
        try {
          const pt = this.leafletMap.latLngToContainerPoint([centroid[0], centroid[1]]);
          const px = pt.x, py = pt.y;
          if (px < -20 || px > W + 20 || py < -20 || py > H + 20) return;
          const r = Math.max(4, Math.round((p.total / maxTotal) * 26));
          bubbles.push({ pid: name, x: px, y: py, r, shortLabel: name.length > 4 ? name.slice(0, 4) : name, ...p });
        } catch (e) {
          /* skip */
        }
      });
      this.bubbles = bubbles;
    },

    showBubbleTip(e, b) {
      const wrap = this.$refs.mapWrap;
      const rect = wrap ? wrap.getBoundingClientRect() : { left: 0, top: 0 };
      this.tooltip = {
        show: true,
        x: e.clientX - rect.left + 16,
        y: e.clientY - rect.top - 60,
        province: b.province,
        region: b.region,
        total: b.total
      };
    },
    hideTip() {
      this.tooltip.show = false;
    },

    onProvinceClick(b) {
      this.selectedProv = { ...b };
    },
    onRankClick(p) {
      this.selectedProv = { ...p };
      const centroid = PROVINCE_CENTROIDS[p.province];
      if (centroid && this.leafletMap) {
        this.leafletMap.flyTo(centroid, 9, { duration: 0.8 });
      }
    },

    mapZoomIn() {
      this.leafletMap && this.leafletMap.zoomIn();
    },
    mapZoomOut() {
      this.leafletMap && this.leafletMap.zoomOut();
    },
    mapFit() {
      this.leafletMap && this.leafletMap.fitBounds([[5.5, 97.3], [20.5, 105.7]], { padding: [8, 8] });
    }
  }
};
</script>

<style scoped>
.geo-map-panel {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e4e4e7;
  overflow: hidden;
  font-family: "Sarabun", sans-serif;
}

.map-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 1px solid #e4e4e7;
  flex-wrap: wrap;
  gap: 8px;
}
.map-head-left { display: flex; align-items: center; }
.map-panel-title { font-size: 0.82rem; font-weight: 700; color: #263238; }
.map-head-right { display: flex; align-items: center; gap: 8px; }

.zoom-btns { display: flex; gap: 2px; }
.zoom-btn {
  width: 26px; height: 26px; border-radius: 6px;
  border: 1px solid #d1d5db; background: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.1s;
}
.zoom-btn:hover { background: #e8f5e9; }
.zoom-btn .v-icon { color: #374151 !important; }

.map-body { display: flex; min-height: 440px; }
.map-wrap { flex: 1; position: relative; min-height: 440px; }
.th-map { width: 100%; height: 100%; min-height: 440px; }

.bubble-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 400; }
.bubble-circle { pointer-events: all; transition: r 0.2s, opacity 0.2s; }
.bubble-circle:hover { opacity: 1 !important; }
.bubble-label { font-size: 9px; fill: white; font-weight: 700; pointer-events: none; font-family: "Sarabun", sans-serif; }

.map-tooltip {
  position: absolute; background: #fff; border: 1px solid #e4e4e7; border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12); min-width: 160px; z-index: 800;
  pointer-events: none; font-family: "Sarabun", sans-serif;
}
.tip-header { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px 6px; border-bottom: 1px solid #f5f5f5; }
.tip-province { font-size: 0.85rem; font-weight: 700; color: #263238; }
.tip-region { font-size: 0.65rem; color: #6b7280; }
.tip-body { padding: 6px 12px 10px; }
.tip-row { display: flex; justify-content: space-between; font-size: 0.75rem; color: #4b5563; }
.tip-row strong { font-weight: 700; color: #263238; }

.tip-fade-enter-active, .tip-fade-leave-active { transition: opacity 0.15s; }
.tip-fade-enter, .tip-fade-leave-to { opacity: 0; }

.selected-info {
  position: absolute; bottom: 12px; left: 12px; background: #fff;
  border: 1px solid #e8f5e9; border-left: 3px solid #2e7d32; border-radius: 8px;
  padding: 8px 12px; z-index: 700; font-family: "Sarabun", sans-serif; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.sel-title { display: flex; align-items: center; font-size: 0.78rem; color: #263238; margin-bottom: 5px; }
.sel-stats { display: flex; gap: 12px; margin-bottom: 5px; }
.sel-stat { font-size: 0.72rem; color: #6b7280; display: flex; flex-direction: column; }
.sel-stat strong { font-size: 0.82rem; color: #263238; }
.sel-actions { display: flex; align-items: center; justify-content: space-between; }

.map-side { width: 240px; border-left: 1px solid #e4e4e7; display: flex; flex-direction: column; background: #fafafa; overflow-y: auto; flex-shrink: 0; }

.ranking-box { padding: 10px 14px; border-bottom: 1px solid #e4e4e7; flex: 1; }
.ranking-title { font-size: 0.72rem; font-weight: 700; color: #374151; margin-bottom: 8px; display: flex; align-items: center; }
.ranking-list { display: flex; flex-direction: column; gap: 3px; }
.rank-row { display: flex; align-items: center; gap: 5px; padding: 4px 6px; border-radius: 5px; cursor: pointer; transition: background 0.1s; }
.rank-row:hover { background: #e8f5e9; }
.rank-row--active { background: #e8f5e9; }
.rank-num { width: 16px; height: 16px; border-radius: 50%; background: #f5f5f5; color: #6b7280; font-size: 0.62rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.rank-num--top { background: #e8f5e9; color: #1b5e20; }
.rank-name { font-size: 0.72rem; color: #263238; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rank-bar-wrap { width: 40px; height: 4px; background: #f5f5f5; border-radius: 999px; overflow: hidden; flex-shrink: 0; }
.rank-bar-fill { height: 100%; border-radius: 999px; transition: width 0.3s; }
.rank-val { font-size: 0.65rem; font-weight: 700; min-width: 24px; text-align: right; flex-shrink: 0; color: #1b5e20; }

.map-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: 10px 14px; }
.map-stat-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 7px; padding: 7px 9px; text-align: center; }
.map-stat-val { font-size: 1.05rem; font-weight: 800; font-family: "IBM Plex Mono", monospace; }
.map-stat-label { font-size: 0.6rem; color: #6b7280; margin-top: 1px; }

@media (max-width: 900px) {
  .map-body { flex-direction: column; }
  .map-side { width: 100%; border-left: none; border-top: 1px solid #e4e4e7; max-height: 260px; }
  .map-stats-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
